import { useCallback, useEffect, useRef, useMemo } from "react";
import { Button } from "@rg/ui/components/Button";
import ExternalLinkIcon from "@/common/images/icons/external-link.svg?react";
import { SocialBadge } from "@/components/social-badge";
import { getSocialNameFromUrl } from "@/common/utils/socialNetwork";
import "./AdCreator.scss";

export interface IAdCreator {
  image: string;
  name: string;
  link: string;
  ctaText?: string;
  socialName?: string;
  handleClick?: () => void;
  handleView?: () => void;
}

export const AdCreator = ({
  image,
  name,
  link,
  ctaText = "Visit Page",
  socialName,
  handleClick,
  handleView
}: IAdCreator) => {
  const ref = useRef<HTMLDivElement | null>(null);

  const onClick = useCallback(() => {
    handleClick?.();
    window.open(link, "_blank");
  }, [link, handleClick]);

  const resolvedSocialName = useMemo(
    () => socialName ?? getSocialNameFromUrl(link) ?? undefined,
    [socialName, link],
  );

  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const options = {
      rootMargin: "0px",
      threshold: 1,
    };

    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          ref.current && handleView && handleView();
          observer.disconnect();
        }
      });
    }, options);

    observer.observe(ref.current);

    return () => {
      observer.disconnect();
    };
  }, [ref]);

  return (
    <div className="AdCreator" ref={ref}>
      <div className="creatorImage">
        <img src={image} alt={name} />
        {resolvedSocialName && <SocialBadge name={resolvedSocialName} className="badgeIcon" />}
      </div>
      <div className="creatorTitleWrapper">
        <span className="creatorTitle">{name}</span>
        <Button className="creatorLink" fullWidth onClick={onClick} size="md" variant="secondary" iconRight={<ExternalLinkIcon />}>{ctaText}</Button>
      </div>
    </div>
  );
};
